import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [submitted, setSubmitted] = useState(false);

  return (
    <footer className="bg-primary text-white font-body">
      <div className="container mx-auto px-4 py-16 max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
        {/* Left Column: Info */}
        <div className="space-y-10">
          {/* Logo */}
          <div className="flex justify-center md:justify-start">
            <img
              src="https://vibe.filesafe.space/1780604500184904220/attachments/b269428a-1e68-45f3-a350-7ebcb8f83069.png"
              alt="WagRight Dog Training"
              className="h-20 w-auto mb-4"
              loading="lazy"
              decoding="async"
            />
          </div>

          {/* Contact */}
          <div className="space-y-4 font-medium text-sm border-l-2 border-accent pl-6">
            <a
              href="tel:+447427900158"
              className="flex items-center gap-3 hover:text-accent transition-colors"
            >
              <Phone className="w-5 h-5 text-accent" /> +44 7427 900158
            </a>
            {/* Email removed as per request */}

            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-accent shrink-0 mt-0.5" />
              <div>
                Nottingham
                <br />
                Nottinghamshire and global
              </div>
            </div>
          </div>

          {/* Text & Socials */}
          <div className="space-y-4">
            <div className="font-bold tracking-wide font-display text-xl uppercase">
              LET'S CONNECT
            </div>
            <p className="text-white/80 leading-relaxed max-w-sm">
              Follow us for training tips, success stories, and real-life dog
              moments!
            </p>
            <div className="flex gap-4 pt-2">
              <a
                href="#"
                aria-label="Facebook"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/christine905012?igsh=YWhwNjVsbWNuNjh3"
                aria-label="Instagram"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@wagrightdogtraini?_r=1&_t=ZN-98H64yCJiRq"
                aria-label="TikTok"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                </svg>
              </a>
              <a
                href="https://www.youtube.com/channel/UCsNPZCw1NPzylVmJpPSpPIQ"
                aria-label="YouTube"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                  <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Right Column: Contact Form */}
        <div className="bg-white/5 p-8 rounded-xl border border-white/10">
          <h3 className="text-2xl font-display font-bold uppercase mb-6 text-accent">
            Send us a message
          </h3>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const name = (formData.get("name") as string) || "";
              const email = (formData.get("email") as string) || "";
              const phone = (formData.get("phone") as string) || "";
              const dogName = (formData.get("dogName") as string) || "";
              const breed = (formData.get("breed") as string) || "";
              const message = (formData.get("message") as string) || "";

              // Push lead to CRM
              fetch("https://backend.leadconnectorhq.com/vibe-ai/lead/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  locationId: "LybUg0eKBAfPYJVWJI73",
                  firstName: name.split(" ")[0] || name,
                  lastName: name.split(" ").slice(1).join(" ") || "",
                  email,
                  phone,
                  notes: message,
                  type: "footer_contact_form",
                  customFields: [
                    {
                      id: "O9O2OlIDG5IOnYvbnm4Z",
                      key: "contact.dog_name",
                      field_value: dogName,
                    },
                    {
                      id: "kJHx3NkYKJ3SKyQ4X81u",
                      key: "contact.breed",
                      field_value: breed,
                    },
                  ],
                }),
              }).catch(() => {});

              // Also fire tracking event
              fetch(
                "https://backend.leadconnectorhq.com/external-tracking/events",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    version: "2021-07-28",
                  },
                  body: JSON.stringify({
                    type: "external_form_submission",
                    timestamp: Date.now(),
                    formId: "Footer Contact Form",
                    formData: {
                      first_name: name,
                      email,
                      phone,
                      "contact.dog_name": dogName,
                      "contact.breed": breed,
                      message,
                    },
                    formLabels: {
                      first_name: "Name",
                      email: "Email Address",
                      phone: "Phone Number",
                      "contact.dog_name": "Dog's Name",
                      "contact.breed": "Breed / Mix",
                      message: "Message",
                    },
                    url: window.location.href,
                    title: document.title,
                    path: window.location.pathname,
                    userAgent: navigator.userAgent,
                    trackingId: "tk_d46c24c393554163a1c490060dfe2722",
                    locationId: "LybUg0eKBAfPYJVWJI73",
                    sessionId: crypto.randomUUID(),
                    properties: {
                      deviceType: /Mobile|Android|iPhone/i.test(
                        navigator.userAgent,
                      )
                        ? "mobile"
                        : "desktop",
                    },
                  }),
                },
              ).catch(() => {});

              e.currentTarget.reset();
              setSubmitted(true);
              setTimeout(() => setSubmitted(false), 5000);
            }}
          >
            {submitted ? (
              <div className="flex items-center justify-center gap-3 py-8 text-accent">
                <CheckCircle2 className="w-6 h-6" />
                <span className="font-semibold text-lg">
                  Thank you! Your message has been sent.
                </span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    name="name"
                    placeholder="Your Name"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    required
                  />
                  <Input
                    name="email"
                    placeholder="Email Address"
                    type="email"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    name="phone"
                    placeholder="Phone Number"
                    type="tel"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    required
                  />
                  <Input
                    name="dogName"
                    placeholder="Dog's Name"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <Input
                  name="breed"
                  placeholder="Breed / Mix"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
                <Textarea
                  name="message"
                  placeholder="How can we help?"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 min-h-[100px]"
                  required
                />
                <Button
                  type="submit"
                  className="w-full bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide h-12"
                >
                  SEND YOUR ENQUIRY <Send className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}
          </form>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#f8f9fa] text-primary py-4 text-xs font-medium">
        <div className="container mx-auto px-4 max-w-7xl flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-wrap justify-center md:justify-start items-center gap-4">
            <span>© WagRight Dog Training, {currentYear}</span>
            <span className="hidden md:inline">|</span>
            <Link to="/privacy" className="hover:text-accent transition-colors">
              Privacy Policy
            </Link>
            <span className="hidden md:inline">|</span>
            <Link to="/terms" className="hover:text-accent transition-colors">
              Website Terms and Conditions
            </Link>
            <span className="hidden md:inline">|</span>
            <Link
              to="/service-terms"
              className="hover:text-accent transition-colors"
            >
              Service Terms
            </Link>
          </div>
          <div>Built By DogTrainerPRO</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
